import classnames from '../../../core/utils/classnames.tsx';
import * as React from 'react';
import { createContext, useContext } from 'react';
import { withIcon } from '../../../core/components/Icon.tsx';
import { faArrowUpRightFromSquare, faChevronDown, faChevronUp } from '@fortawesome/pro-regular-svg-icons';
import { useAssistanceContext } from '../../../document/pages/Assistance/AssistanceContext.tsx';
import { useTranslation } from 'react-i18next';
import { useControllableState } from '../../../core/utils/useControllableState.tsx';
import { useKeywordCounts } from './HighlightText.tsx';
import Tooltip from '../../../core/components/Tooltip.tsx';

const ChevronDownIcon = withIcon(faChevronDown);
const ChevronUpIcon = withIcon(faChevronUp);
const ExternalLinkIcon = withIcon(faArrowUpRightFromSquare);

interface ChapterContextProps {
    canExpand?: boolean;
    expanded?: boolean;
    onExpandedChange?: (expanded: boolean) => void;
}

const ChapterContext = createContext<ChapterContextProps>({});
export const useChapterContext = () => useContext(ChapterContext);

const ChapterHeader = ({
    className,
    children,
    pageIndex,
    controls,
    isMain,
    isSectionTitle,
    containsHighlightKeywords, // from backend when collapsed
    highlightKeywords,
    highlightText,
}: React.ComponentPropsWithRef<'div'> & {
    pageIndex?: number;
    controls?: React.ReactNode;
    containsHighlightKeywords?: boolean; // from backend when collapsed
    highlightKeywords?: string[];
    highlightText?: string;
    isMain?: boolean;
    isSectionTitle?: boolean;
}) => {
    const { t } = useTranslation('assistance');
    const { record } = useAssistanceContext();

    const highlightKeywordCounts = useKeywordCounts(highlightText, highlightKeywords);
    const sortedHighlightKeywords = Object.entries(highlightKeywordCounts)
        .sort((a, b) => b[1] - a[1])
        .map(([keyword, count]) => keyword);
    const totalHighlightKeywords = Object.values(highlightKeywordCounts).reduce((acc, count) => acc + count, 0);

    const { canExpand, expanded, onExpandedChange } = useChapterContext();

    return (
        <div
            className={classnames(
                'flex justify-between mb-2 w-full items-center py-3 px-4 transition-colors top-0 sticky z-10 border-line-weak',
                className,
                isMain ? '!top-0 z-30' : ''
            )}
        >
            <div className={classnames('flex justify-between items-center', !isSectionTitle && 'w-3/5')}>
                <div
                    className={classnames(
                        'font-semibold text-content-strong text-base w-fit flex',
                        canExpand && 'cursor-pointer'
                    )}
                    onClick={() => canExpand && onExpandedChange(!expanded)}
                >
                    {!isSectionTitle && (
                        <div className={classnames('w-10 text-center', !canExpand && 'invisible')}>
                            {expanded ? (
                                <ChevronUpIcon className="text-content-strong text-sm" />
                            ) : (
                                <ChevronDownIcon className="text-content-strong text-sm" />
                            )}
                        </div>
                    )}
                    {children}
                    {isSectionTitle && (
                        <div className={classnames('w-10 text-center', !canExpand && 'invisible')}>
                            {expanded ? (
                                <ChevronUpIcon className="text-content-strong text-sm" />
                            ) : (
                                <ChevronDownIcon className="text-content-strong text-sm" />
                            )}
                        </div>
                    )}
                </div>

                <div className="flex gap-4 items-center ml-auto">
                    {expanded && totalHighlightKeywords > 0 && (
                        <Tooltip
                            content={
                                sortedHighlightKeywords.length > 0 ? (
                                    <ul className="list-disc pl-3">
                                        {sortedHighlightKeywords.map((keyword) => (
                                            <li key={keyword}>{keyword}</li>
                                        ))}
                                    </ul>
                                ) : null
                            }
                            sideOffset={5}
                        >
                            <span className="text-sm bg-[#c5ffed]/30 inline-flex rounded items-baseline px-1.5 py-0.5 gap-2 font-medium border border-solid border-[#a0d9c7] text-content-brand-default cursor-help">
                                {t('listOfServicesView.highlightKeywordsBadge', {
                                    count: totalHighlightKeywords,
                                })}
                            </span>
                        </Tooltip>
                    )}

                    {!expanded && containsHighlightKeywords && (
                        <span
                            className="text-sm bg-[#c5ffed]/30 inline-flex rounded items-baseline px-1.5 py-0.5 gap-2 font-medium border border-solid border-[#a0d9c7] text-content-brand-default cursor-pointer"
                            onClick={() => onExpandedChange(!expanded)}
                        >
                            {t('listOfServicesView.containsHighlightKeywordsBadge')}
                        </span>
                    )}

                    {pageIndex !== undefined && (
                        <a
                            className="font-medium text-sm text-content-brand-default hover:text-content-brand-hover"
                            href={`${record?.inputFile?.url}#page=${Number(pageIndex) + 1}`}
                            target="_blank"
                        >
                            {t('listOfServicesView.page')} {Number(pageIndex) + 1}
                            <ExternalLinkIcon className={classnames('ml-2')} />
                        </a>
                    )}
                </div>
            </div>

            <div>{controls}</div>
        </div>
    );
};

const ChapterContent = ({ className, children }: React.ComponentPropsWithoutRef<'div'>) => {
    const { expanded } = useChapterContext();

    return (
        <div
            className={classnames(
                'grid transition-all duration-300 ease-in-out origin-top',
                expanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
            )}
        >
            <div
                className={classnames(
                    'flex flex-col min-h-0 transition-all duration-300 overflow-auto',
                    expanded ? 'pb-2 opacity-100' : 'mb-0 overflow-hidden opacity-0',
                    className
                )}
            >
                {children}
            </div>
        </div>
    );
};

const ChapterRoot = ({
    children,
    className,
    canExpand = true,
    expanded: propsExpanded,
    defaultExpanded = true,
    onExpandedChange: propsSetExpanded,
    ...props
}: React.ComponentPropsWithoutRef<'div'> & ChapterContextProps & { defaultExpanded?: boolean }) => {
    const [expanded, setExpanded] = useControllableState(defaultExpanded, propsExpanded, propsSetExpanded);
    return (
        <ChapterContext.Provider
            value={{ canExpand, expanded: canExpand ? expanded : false, onExpandedChange: setExpanded }}
        >
            <div className={classnames('w-full gap-4 text-content-strong', className)} {...props}>
                {children}
            </div>
        </ChapterContext.Provider>
    );
};

export default Object.assign(ChapterRoot, {
    Header: ChapterHeader,
    Content: ChapterContent,
});
